import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import TourIcon from '@mui/icons-material/AssistantDirection';
import { currentLang, useT } from '../../i18n';
import GuideContent, { type HelpGuideData } from './GuideContent';
import GuidedTour, { type TourStep } from './GuidedTour';

/**
 * Ayuda en cada pantalla. Al cambiar de pantalla se piden sus guías (`GET help/screen?path=`):
 *   - El botón ? de la barra superior abre un panel lateral con la guía de esa pantalla (pasos y video).
 *   - Si la guía trae recorrido para la pantalla y el usuario no lo ha visto, el recorrido arranca solo.
 * Las guías salen de ruttia-platform/docs/guias (campos `pantallas` y `recorrido`).
 */
export interface ScreenGuide {
  topic: string;
  title: string;
  hasVideo: boolean;
  tour: TourStep[];
}

interface ScreenHelpValue {
  guides: ScreenGuide[];
  openHelp: () => void;
  startTour: (topic: string) => void;
}

const ScreenHelpContext = createContext<ScreenHelpValue>({ guides: [], openHelp: () => undefined, startTour: () => undefined });
export const useScreenHelp = () => useContext(ScreenHelpContext);

/** La primera vez que se entra a una pantalla con recorrido, espera a que la pantalla cargue. */
const AUTO_TOUR_DELAY_MS = 1200;

export const ScreenHelpProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const t = useT();
  const { pathname } = useLocation();
  const [guides, setGuides] = useState<ScreenGuide[]>([]);
  const [seen, setSeen] = useState<Set<string> | null>(null);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<HelpGuideData | null>(null);
  const [detailTopic, setDetailTopic] = useState<string | null>(null);
  const [tour, setTour] = useState<{ topic: string; steps: TourStep[] } | null>(null);
  const autoStarted = useRef<Set<string>>(new Set());

  // Recorridos que el usuario ya vio (una vez por sesión de la web).
  useEffect(() => {
    axios
      .get<{ data: string[] }>('help/tours/seen')
      .then(({ data }) => setSeen(new Set(data.data)))
      .catch(() => setSeen(new Set()));
  }, []);

  // Guías de la pantalla abierta.
  useEffect(() => {
    let active = true;
    setGuides([]);
    setOpen(false);
    setTour(null);
    axios
      .get<{ data: ScreenGuide[] }>('help/screen', { params: { path: pathname, lang: currentLang() } })
      .then(({ data }) => active && setGuides(data.data))
      .catch(() => active && setGuides([]));
    return () => {
      active = false;
    };
  }, [pathname]);

  const markSeen = useCallback((topic: string) => {
    setSeen((prev) => {
      const next = new Set<string>(Array.from(prev ?? []));
      next.add(topic);
      return next;
    });
    axios.post(`help/tours/${encodeURIComponent(topic)}/seen`).catch(() => undefined);
  }, []);

  const startTour = useCallback(
    (topic: string) => {
      const g = guides.find((x) => x.topic === topic);
      if (g?.tour.length) {
        setOpen(false);
        setTour({ topic, steps: g.tour });
      }
    },
    [guides],
  );

  // Recorrido automático: la primera vez que el usuario entra a una pantalla que lo tiene.
  useEffect(() => {
    if (!seen || tour) return undefined;
    const pending = guides.find((g) => g.tour.length && !seen.has(g.topic) && !autoStarted.current.has(`${g.topic}:${pathname}`));
    if (!pending) return undefined;
    const timer = window.setTimeout(() => {
      autoStarted.current.add(`${pending.topic}:${pathname}`);
      setTour({ topic: pending.topic, steps: pending.tour });
    }, AUTO_TOUR_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [guides, seen, tour, pathname]);

  const loadDetail = useCallback((topic: string) => {
    setDetailTopic(topic);
    setDetail(null);
    axios
      .get<HelpGuideData>(`help/${encodeURIComponent(topic)}`, { params: { lang: currentLang() } })
      .then(({ data }) => setDetail(data))
      .catch(() => setDetail(null));
  }, []);

  const openHelp = useCallback(() => {
    setOpen(true);
    if (guides[0] && detailTopic !== guides[0].topic) loadDetail(guides[0].topic);
  }, [guides, detailTopic, loadDetail]);

  const value = useMemo(() => ({ guides, openHelp, startTour }), [guides, openHelp, startTour]);
  const current = guides.find((g) => g.topic === detailTopic);

  return (
    <ScreenHelpContext.Provider value={value}>
      {children}
      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        // Por encima de la barra superior (que va sobre el menú lateral), para que se vea su encabezado.
        sx={{ zIndex: (theme) => theme.zIndex.drawer + 2 }}
        PaperProps={{ sx: { width: { xs: '100vw', sm: 480, xl: 560 }, maxWidth: '100vw', bgcolor: 'background.paper' } }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1, minWidth: 0 }}>
              {t('screenHelp.title')}
            </Typography>
            <IconButton aria-label={t('common.close')} onClick={() => setOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Stack>
          <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2, minWidth: 0 }}>
            {guides.length > 1 && (
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
                {guides.map((g) => (
                  <Chip
                    key={g.topic}
                    label={g.title}
                    color={g.topic === detailTopic ? 'primary' : 'default'}
                    variant={g.topic === detailTopic ? 'filled' : 'outlined'}
                    onClick={() => loadDetail(g.topic)}
                  />
                ))}
              </Stack>
            )}
            {current && (
              <>
                <Typography component="h2" variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                  {current.title}
                </Typography>
                {current.tour.length > 0 && (
                  <Button size="small" variant="outlined" startIcon={<TourIcon />} sx={{ mt: 1 }} onClick={() => startTour(current.topic)}>
                    {t('screenHelp.startTour')}
                  </Button>
                )}
              </>
            )}
            {detail ? (
              <GuideContent guide={detail} compact onVideoExpired={() => detailTopic && loadDetail(detailTopic)} />
            ) : (
              <>
                <Skeleton variant="rounded" height={200} sx={{ my: 2 }} />
                <Skeleton variant="text" />
                <Skeleton variant="text" width="80%" />
              </>
            )}
          </Box>
          <Divider />
          <Box sx={{ px: 2, py: 1.5 }}>
            <Button component={RouterLink} to="/ayuda" size="small" onClick={() => setOpen(false)}>
              {t('screenHelp.allGuides')}
            </Button>
          </Box>
        </Box>
      </Drawer>
      {tour && (
        <GuidedTour
          steps={tour.steps}
          onFinish={() => {
            markSeen(tour.topic);
            setTour(null);
          }}
        />
      )}
    </ScreenHelpContext.Provider>
  );
};
